'use client';

import { useState, useRef, useEffect, useCallback } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface PropertyContext {
  postcode: string;
  propertyType: string;
  price: number;
}

interface Props {
  context: PropertyContext;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function extractDistrict(postcode: string): string {
  const m = postcode.trim().toUpperCase().match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)/);
  return m ? m[1] : postcode;
}

function formatPrice(price: number): string {
  return '£' + (price >= 1_000_000
    ? (price / 1_000_000).toFixed(price % 1_000_000 === 0 ? 0 : 1) + 'm'
    : Math.round(price / 1000) + 'k');
}

function getSuggestedQuestions(ctx: PropertyContext): string[] {
  const d = extractDistrict(ctx.postcode);
  return [
    `How has ${d} performed over the last 10 years?`,
    `Is ${formatPrice(ctx.price)} a fair price for a ${ctx.propertyType} in ${d}?`,
    `What similar areas to ${d} are showing early gentrification signs?`,
    `Compare ${d} to the top gentrifying districts in the UK`,
  ];
}

// ── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsChat({ context }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      const userMsg: Message = { role: 'user', content: text.trim() };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      setInput('');
      setIsLoading(true);

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: newMessages,
            context: {
              postcode: context.postcode,
              propertyType: context.propertyType,
              price: context.price,
            },
          }),
        });

        if (!res.ok) throw new Error('Chat request failed');

        const data = await res.json();
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.text },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              'Sorry, I was unable to analyse this area right now. Please try again.',
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, context],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const suggested = getSuggestedQuestions(context);
  const showSuggestions = messages.length === 0 && !isLoading;

  return (
    <div className="flex flex-col rounded-xl border border-black/10 bg-white">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-black/10 px-4 py-3">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#d4ede5]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-[#1a4a3a]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <span className="text-sm font-semibold text-[#3a3935]">
          Ask about this area
        </span>
        <span className="rounded-md bg-[#d4ede5] px-1.5 py-0.5 text-[10px] font-bold text-[#1a4a3a]">
          AI
        </span>
      </div>

      {/* Messages area */}
      <div
        ref={scrollRef}
        className="flex max-h-72 min-h-[120px] flex-col gap-3 overflow-y-auto px-4 py-3"
      >
        {showSuggestions && (
          <div className="space-y-2">
            <p className="text-xs text-[#7a7870]">
              Ask me anything about {extractDistrict(context.postcode)} — price
              trends, predictions, gentrification, area comparisons...
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggested.map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="rounded-full border border-black/10 bg-[#f3f0ea] px-3 py-1.5 text-left text-xs text-[#3a3935] transition hover:border-[#2d7a5f]/40 hover:bg-[#d4ede5] hover:text-[#1a4a3a]"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-[#1a4a3a] text-white'
                  : 'bg-[#f3f0ea] text-[#3a3935]'
              }`}
            >
              {msg.role === 'assistant' ? (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1.5 rounded-2xl bg-[#f3f0ea] px-4 py-3">
              <div className="h-2 w-2 animate-bounce rounded-full bg-[#7a7870] [animation-delay:-0.3s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-[#7a7870] [animation-delay:-0.15s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-[#7a7870]" />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 border-t border-black/10 px-3 py-2"
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about price trends, predictions, gentrification..."
          disabled={isLoading}
          className="flex-1 rounded-lg border border-black/15 bg-[#faf8f4] px-3 py-2 text-sm text-[#0f0f0d] placeholder:text-[#7a7870] focus:border-[#2d7a5f] focus:outline-none focus:ring-1 focus:ring-[#d4ede5] disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#1a4a3a] text-white transition hover:bg-[#2d7a5f] disabled:opacity-40"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 19V5m0 0l-7 7m7-7l7 7"
            />
          </svg>
        </button>
      </form>
    </div>
  );
}
